import { useMemo } from 'react';
import { css } from '@emotion/react';
import {
  AppointmentAggregatesResponse,
  AppointmentConfirmationStatus,
  AppointmentStatus,
  AtRiskStatus,
} from '@weave/schema-gen-ts/dist/schemas/analytics-reporting/analytics_gateway/analytics_gateway.pb';
import { DataCard, DataCardProps } from '@frontend/analytics-shared';
import { useTranslation } from '@frontend/i18n';
import { formatters } from '@frontend/shared';
import { theme } from '@frontend/theme';
import { useAppointmentAnalyticsV2ShallowStore } from '../../hooks/use-appointment-analytics-v2-store';

interface Props {
  aggregatesData: AppointmentAggregatesResponse | undefined;
  aggregatesDataLoading: boolean;
}

export const UpcomingAppointmentsDataCards = (props: Props) => {
  const { aggregatesData, aggregatesDataLoading } = props;
  const { applyFiltersAndSwitchToTableView } = useAppointmentAnalyticsV2ShallowStore(
    'applyFiltersAndSwitchToTableView'
  );
  const { t } = useTranslation('analytics');
  const data = aggregatesData?.data ? aggregatesData.data[0] : undefined;

  const appointmentsScheduled = data?.totalAppointments ?? 0;
  const unconfirmedAppointments = data?.unconfirmedAppointments ?? 0;
  const atRiskAppointments = data?.atRiskAppointments ?? 0;

  const upcomingCardsData: DataCardProps[] = useMemo(
    () => [
      {
        label: t('Appointments Scheduled'),
        value: formatters.value(appointmentsScheduled),
        iconData: {
          name: 'calendar',
          color: theme.colors.primary50,
          bgColor: theme.colors.primary5,
        },
        size: 'large',
        isLoading: aggregatesDataLoading,
        infoTipData: {
          description: t('Total number of upcoming appointments'),
        },
      },
      {
        label: t('Unconfirmed Appointments'),
        value: formatters.value(unconfirmedAppointments),
        iconData: {
          name: 'pending',
          color: theme.colorsV2.yellow40(),
          bgColor: theme.colorsV2.yellow20(25),
        },
        ctaConfig: {
          icon: 'trend-up',
          text: 'View',
          onClick: () => {
            applyFiltersAndSwitchToTableView({
              confirmationStatus: AppointmentConfirmationStatus.APPOINTMENT_CNF_STATUS_UNCONFIRMED,
            });
          },
        },
        size: 'large',
        isLoading: aggregatesDataLoading,
        infoTipData: {
          description: t('Total number of upcoming appointments that are still pending confirmation.'),
        },
      },
      {
        label: t('At Risk Appointments'),
        value: formatters.value(atRiskAppointments),
        iconData: {
          name: 'alert',
          color: theme.colors.critical50,
          bgColor: theme.colors.critical10,
        },
        ctaConfig: {
          icon: 'trend-up',
          text: 'View',
          onClick: () => {
            applyFiltersAndSwitchToTableView({
              atRiskStatus: [
                AtRiskStatus.AT_RISK_STATUS_REPEAT_CANCEL,
                AtRiskStatus.AT_RISK_STATUS_REPEAT_NSR,
                AtRiskStatus.AT_RISK_STATUS_REPEAT_BOTH,
              ],
              status: [AppointmentStatus.APPOINTMENT_STATUS_AT_RISK],
            });
          },
        },
        size: 'large',
        isLoading: aggregatesDataLoading,
        infoTipData: {
          description: t(
            'Appointments for patients that had more than 2 no shows or cancellations in the last 12 months'
          ),
        },
      },
    ],
    [
      unconfirmedAppointments,
      atRiskAppointments,
      aggregatesDataLoading,
      appointmentsScheduled,
      applyFiltersAndSwitchToTableView,
      t,
    ]
  );

  return (
    <div css={styles.cardsGrid}>
      {upcomingCardsData.map((card) => (
        <DataCard key={card.label} {...card} />
      ))}
    </div>
  );
};

const styles = {
  cardsGrid: css`
    display: flex;
    flex-wrap: wrap;
    gap: ${theme.spacing(2)};
  `,
};
